// A source represents a single reference document chunk used as part of a message response
// It is represented one or more times in the text, using the citation_number and
// a letter suffix to denote which 'quote' (part) of the chunk was highlighted by the LLM

import { WikiDocument } from 'src/lib/types';
import { ULID } from 'ulid';

export interface Page {
  page_index: number;
  printed_page_number: string;
}

export interface Line {
  id: string;
  text: string;
}

export interface Paragraph {
  id: string;
  type: 'paragraph';
  page: Page;
  lines: Line[];
}

export interface Figure {
  id: string;
  type: 'figure';
  caption: string;
  figure_id: number;
  content_type: string;
  page: Page;
}

export interface Table {
  id: string;
  type: 'table';
  content: string;
  table_format: string;
  caption: string;
  page: Page;
}

export interface CitationPart {
  document_id: string; // ULID
  document_title: string;
  document_date: string;
  // Present unless this chunk is cited more than once in the conversation,
  // in which case the backend hoists the body into the response's
  // top-level `citation_sources` registry (see NewChatConversation) and
  // sets `citation_ref` instead, to avoid repeating identical bytes.
  sources?: (Paragraph | Figure | Table)[];
  citation_ref?: string;
  quotes: string[][];
  // History loads omit `sources` and carry these so the pill can render
  // before the body is fetched on hover.
  page?: string;
  part_id?: string;
  issuing_body: string;
  jurisdiction?: string;
  authority?: string;
}

export interface Source {
  type: 'cite';
  title: string;
  date?: string;
  publication_date?: string;
  page?: string;
  external_id?: string;
  jurisdiction?: string;
  authority?: string;
  sources: (Paragraph | Figure | Table)[];
  issuing_body: string;
  quote?: string[];
  // Set on history-loaded citations. The body is fetched on hover.
  part_id?: string;
}

export interface WebSource {
  type: 'web_cite';
  url: string;
  title: string;
  chunk_id: string;
}

export interface SpreadsheetSource {
  type: 'spreadsheet_cite';
  document_id: string;
  document_title: string;
  sheet_name: string;
  cell_range: string;
}

export type CitationSource = Source | WebSource | SpreadsheetSource;

export function isWebSource(source: CitationSource): source is WebSource {
  return source.type === 'web_cite';
}

export function isSpreadsheetSource(source: CitationSource): source is SpreadsheetSource {
  return source.type === 'spreadsheet_cite';
}

export interface ParentPart {
  type:
    | 'basic'
    | 'draft'
    | 'thought'
    | 'image'
    | 'mermaid'
    | 'vega'
    | 'feature_link'
    | 'tool_call'
    | 'file'
    | 'tagged_resource';
  content: string;
}

export interface TaggedResourcePart extends ParentPart {
  type: 'tagged_resource';
  ref_id: number;
  resource_type: string;
  title: string;
  resource_id: string;
}

export interface DraftPart extends ParentPart {
  type: 'draft';
  title: string;
  streaming?: boolean;
  canvas_id?: string;
  // Presence is the signal for both card buttons ("View version {from}" and
  // "Show changes"): the before→after version pair for this card's edit.
  // Populated by the ChatMessageItemTs dedupe scan.
  diff?: { readonly from: number; readonly to: number };
}

export interface MermaidPart extends ParentPart {
  type: 'mermaid';
  title: string;
  streaming?: boolean;
}

export interface VegaPart extends ParentPart {
  type: 'vega';
  title: string;
  streaming?: boolean;
}

export interface ImagePart extends ParentPart {
  type: 'image';
  filename: string;
  url: string;
  /** Browser-renderable JPEG rendition, tried only when `url` (e.g. HEIC) fails to decode. */
  fallback_url?: string;
  expiry?: string; // ISO datetime when the URL expires
  /** True while the image is still being uploaded/processed on the server. */
  processing?: boolean;
  /** Document Hub document ULID — present for hub-uploaded images. */
  document_id?: string;
  /**
   * Per-upload id (the uploads Map key) tying a pending part to its upload.
   * Client-only and transient: pending message parts are never serialised to
   * the request (the wire carries `file_id`s), so it keys post-upload
   * hydration without a by-filename collision when two files share a name.
   */
  uploadKey?: string;
}

export interface ThoughtPart extends ParentPart {
  type: 'thought';
}

export interface FeatureLinkPart extends ParentPart {
  type: 'feature_link';
  feature: string;
  mode: string;
  template_code?: string;
  filestem?: string;
  documents?: Array<{
    document_id: string;
    filestem: string;
  }>;
}

export interface ToolCallPart extends ParentPart {
  type: 'tool_call';
  id: string;
  name: string;
  arguments: any;
  status: 'running' | 'success' | 'error';
  index: number;
  metadata?: Record<string, any>;
}

export interface FilePart extends ParentPart {
  type: 'file';
  id?: string;
  filename: string;
  url?: string;
  /** True while the file is still being uploaded/processed on the server. */
  processing?: boolean;
  /** Per-upload id keying post-upload hydration; see {@link ImagePart.uploadKey}. */
  uploadKey?: string;
}

/**
 * Generic, resource-type-agnostic citation emitted by the agent via
 * `<resource id='N'>…</resource>`. Legacy `file_link_*` stream parts
 * (older agents, persisted chat history) are normalised into this shape
 * inside {@link useChatStream}, so the rest of the frontend only ever
 * deals with `ResourceLink`s.
 */
export interface ResourceLink {
  /**
   * Stable client-side lookup key. Equal to the resource ULID — the
   * frontend never receives the LLM-facing numeric ref_id.
   */
  id: string;
  /** Resource ULID — used for client-side navigation URLs. */
  resource_id: string;
  /** e.g. "document", "chat", "action", "team_group". */
  resource_type: string;
  title: string;
  /** Document-specific: True when the resource has a wiki_documents
   * row. Drives a bespoke "wiki" icon in the chat citation so
   * regulatory wiki entries stand out from freeform document uploads.
   * Optional for backwards compatibility with older streamed payloads.
   */
  is_wiki?: boolean;
}

// 'generating' and 'failed' mark an assistant turn a detached background job
// owns (the board report): the transcript is polled while generating, and the
// turn resolves to completed or failed.
export type MessageStatus = 'streaming' | 'completed' | 'interrupted' | 'stopped' | 'generating' | 'failed';

export type ToolCallDecisionKind = 'approved' | 'rejected';

export interface ToolCallDecision {
  tool_call_id: string;
  tool_name?: string | null;
  decision: ToolCallDecisionKind;
  /** Opaque result payload stored by the tool — e.g. {id, reference_id} for action cards. */
  result?: Record<string, any> | null;
}

export interface Message {
  role: 'user' | 'assistant' | 'tool';
  local_id: number; // used for rendering key in frontend only
  message_id: string | undefined; // undefined before we get completed message from server
  topic?: string;
  session_id?: string;
  hideOptions?: boolean;
  userQuery?: string;
  sources?: CitationSource[];
  resource_links?: ResourceLink[];
  /** Suggested next step from the model (phrased as the user's next message), offered as a ghost prefill in the chat input. */
  follow_up?: string | null;
  related_questions?: any[];
  parts: ParentPart[];
  current_part?: ParentPart | null;
  // Optional writing assistant metadata
  patch?: any[] | null;
  external_document_id?: string | null;
  created_at?: string;
  specialist_id?: string; // ID of the specialist that generated this message
  error?: string; // Error text shown on the message (e.g. "Cancelled", "Stopped")
  /** Client-side state for an assistant response that did not reach completion. */
  status?: MessageStatus;
}

export type Conversation = Message[];

export interface FilterOption {
  label: string;
  value: string;
  count: number;
  disabled: boolean;
}

export type FilterType = 'category' | 'domain' | 'subDomain' | 'document' | 'docHubSource';
export type ChatSearchFilters = Record<FilterType, string[]>;

// ============================================================
// Specialist knowledge base types
// ============================================================

export type KnowledgeBaseType = 'default' | 'specific_categories' | 'specific_documents';

export interface PinnedDocumentRef {
  id: string;
  title: string;
  is_wiki?: boolean;
}

export interface KnowledgeBase {
  type: KnowledgeBaseType;
  categories?: string[];
  doc_hub_sources?: string[];
  web_access?: boolean;
  documents?: PinnedDocumentRef[];
}

/** Starting state for the create-new-specialist form. */
export const BLANK_SPECIALIST_DRAFT = {
  name: '',
  colour: '#8b5cf6',
  prompt_parts: {
    instruction: '',
    response_style: ''
  },
  knowledge_base: { type: 'default' as KnowledgeBaseType },
  tool_set: [] as string[]
};

export interface Chat {
  id: string;
  topic: string;
  timestamp: string;
  shared_by?: UserSummary;
  num_shared_with?: number;
  has_canvas?: boolean;
}

export interface UserSummary {
  firstname: string;
  surname: string;
  organisation: string;
  email: string;
}

export enum ResourcePermissionRole {
  READ = 10,
  COMMENT = 20,
  DATA_ENTRY = 25,
  WRITE = 30,
  SHARE = 40,
  OWNER = 50
}

export interface ResourcePermission {
  permission_id: string | null; // null for inherited-only users
  role: ResourcePermissionRole;
  assignee: UserSummary;
  granter: UserSummary;
  /** Floor from parent grant — user can only be upgraded, not downgraded below this. */
  inherited_permission?: ResourcePermissionRole | null;
}

export type SortField = 'title' | 'category' | 'domain' | 'publication_date';

export interface FilterAggregations {
  category_counts: Record<string, number>;
  domain_counts: Record<string, number>;
  subdomain_counts: Record<string, number>;
  authority_counts: Record<string, number>;
  source_counts: Record<string, number>;
}

export interface TenantShare {
  permission_id: string;
  role: ResourcePermissionRole;
  tenant_name: string;
  granter: UserSummary;
}

export interface SharesResponse {
  user_shares: ResourcePermission[];
  group_shares?: { group_id: string; group_name: string; permission: ResourcePermissionRole }[];
  tenant_share: TenantShare | null;
  all_users_share: AllUsersShare | null;
}

export interface PaginatedDocumentsResponse {
  references: WikiDocument[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CategoryDefaultsResponse {
  categories: Record<string, boolean>;
}

export interface SpecialistPromptPart {
  instruction: string;
  response_style: string;
}

export interface SpecialistCreator {
  external_id: ULID;
  firstname?: string | null;
  surname?: string | null;
  organisation?: string | null;
}

export interface AllUsersShare {
  permission_id: string;
  role: ResourcePermissionRole;
  granter: UserSummary;
}

export interface Specialist {
  id: ULID;
  name: string;
  colour: string; // hex string with hash, e.g. '#ffb3ba'
  is_owner: boolean;
  current_user_role?: ResourcePermissionRole | null;
  creator?: SpecialistCreator | null;
  prompt_parts: SpecialistPromptPart;
  knowledge_base: KnowledgeBase;
  tool_set: string[];
  deleted_at?: string | null; // ISO timestamp when specialist was deleted
  last_used_at?: string | null; // ISO timestamp when specialist was last used by the user
  shared_by?: SpecialistCreator | null; // Who shared this specialist with the current user
}

export interface CanvasResponse {
  id: string;
  title: string;
  content: string;
  content_type: string;
  version_id: number;
  updated_at: string;
}

export interface CanvasSummary {
  id: string;
  title: string;
}

/** A canvas created from a Pending Canvas, with the session it now lives in. */
export interface CreatedCanvas {
  session_id: string;
  canvas: CanvasResponse;
}

export interface CanvasVersion {
  external_document_id: string;
  version_id: number;
  title: string;
  content_type: string;
  created_at: string;
}

export interface CanvasHubExport {
  document_id: string;
  title: string;
  canvas_version_id: number;
}

export interface CanvasVersionContent extends CanvasVersion {
  content: string;
}

// Canvas diff wire contract — an exact snake_case mirror of the backend
// `CanvasDiffResponse` / `BlockDiffSchema` in editor/schemas.py, discriminated
// on `block_type`. The frontend consumes this shape; it does not change it.
export type BlockOp = 'equal' | 'insert' | 'delete' | 'changed';
export type RunOp = 'equal' | 'insert' | 'delete';
export type DiffBlockType = 'paragraph' | 'heading' | 'list_item' | 'code' | 'table' | 'thematic_break' | 'image';
export type DiffContainerKind = 'root' | 'blockquote' | 'list_ordered' | 'list_bullet';

export interface DiffMarks {
  bold: boolean;
  italic: boolean;
  code: boolean;
  underline: boolean;
  link_href: string | null;
}

export interface WordRun {
  op: RunOp;
  text: string;
  marks: DiffMarks;
}

export interface LineRun {
  op: RunOp;
  text: string;
}

export interface BlockContext {
  container: DiffContainerKind;
  depth: number;
  quote_depth: number;
  list_ordinal: number | null;
  heading_level: number | null;
  code_language: string | null;
}

// The wire `from_` alias is emitted as `from`.
export interface TypeChange {
  from: DiffBlockType;
  to: DiffBlockType;
}

export interface CellDiff {
  op: BlockOp;
  column_index: number;
  runs: WordRun[];
  // Old/new author cell colour (#rrggbb), null where unset; a difference drives
  // the colour swatch. Span extents identify the origin cell's rendered span.
  // `covered` marks a grid slot spanned by another origin in the surviving table;
  // covered entries carry no runs and are skipped by the renderer.
  background_from: string | null;
  background_to: string | null;
  colspan: number;
  rowspan: number;
  covered: boolean;
}

export interface ColumnDiff {
  op: BlockOp;
  header: WordRun[];
  index_from: number | null;
  index_to: number | null;
  colspan: number;
  rowspan: number;
  covered: boolean;
}

export interface RowDiff {
  op: BlockOp;
  cells: CellDiff[];
}

export interface TableDiff {
  columns: ColumnDiff[];
  rows: RowDiff[];
}

interface BlockDiffBase {
  op: BlockOp;
  context: BlockContext;
  type_change: TypeChange | null;
  context_from: BlockContext | null;
}

export interface TextBlockDiff extends BlockDiffBase {
  block_type: 'paragraph' | 'heading' | 'list_item';
  runs: WordRun[];
}

export interface CodeBlockDiff extends BlockDiffBase {
  block_type: 'code';
  lines: LineRun[];
}

export interface TableBlockDiff extends BlockDiffBase {
  block_type: 'table';
  table: TableDiff;
}

export interface ThematicBreakDiff extends BlockDiffBase {
  block_type: 'thematic_break';
}

// An image block's ref plus its two caption slots (alt descriptor, title
// caption) and its Display Width (percentage of the column, null at natural size).
export interface ImageRefDiff {
  ref: string;
  alt: string;
  title: string;
  width_pct: number | null;
}

export interface ImageBlockDiff extends BlockDiffBase {
  block_type: 'image';
  ref: string;
  alt: string;
  title: string;
  width_pct: number | null;
  // The prior ref/alt/title/width on a `changed` image, so a ref swap, a caption
  // edit, and a resize are all visible; null on insert/delete/equal.
  image_from: ImageRefDiff | null;
}

export type BlockDiff = TextBlockDiff | CodeBlockDiff | TableBlockDiff | ThematicBreakDiff | ImageBlockDiff;

export interface CanvasDiff {
  from_version_id: number;
  to_version_id: number;
  from_title: string;
  to_title: string;
  blocks: BlockDiff[];
}
